import React, { useState } from 'react';
import {
  Box, Typography, Container, Grid, TextField,
  Button, Paper, Alert, Snackbar
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const contactInfo = [
  { icon: <PhoneIcon />, title: 'Call Us', value: '+91 98765 43210', sub: 'Mon-Sat 9AM - 7PM', color: '#1e90d4' },
  { icon: <WhatsAppIcon />, title: 'WhatsApp', value: '+91 98765 43210', sub: 'Quick Response', color: '#25d366' },
  { icon: <EmailIcon />, title: 'Email Us', value: 'info@cctvpartner.in', sub: 'Reply within 24 hrs', color: '#f0c040' },
  { icon: <LocationOnIcon />, title: 'Visit Us', value: 'Kolhapur, Maharashtra', sub: 'Site Visits Available', color: '#e74c3c' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255,255,255,0.03)',
    '& fieldset': { borderColor: 'rgba(30,144,212,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(30,144,212,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#1e90d4' },
  },
  '& .MuiInputLabel-root': { color: '#8fb3d9' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1e90d4' },
  '& .MuiOutlinedInput-input': { color: '#e8f0fe' },
};

export default function Contact() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.name && form.phone) setSubmitted(true);
  };

  return (
    <Box
      id="contact"
      sx={{
        py: 12,
        background: 'linear-gradient(180deg, #081e35, #061525)',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography sx={{ fontSize: '0.8rem', letterSpacing: '3px', color: '#1e90d4', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>
            Reach Out
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, color: '#e8f0fe', mb: 2 }}>
            Get a Free <Box component="span" sx={{ color: '#1e90d4' }}>Consultation</Box>
          </Typography>
          <Box sx={{ width: 60, height: 3, background: 'linear-gradient(90deg, #1e90d4, transparent)', mx: 'auto', borderRadius: 2, mb: 3 }} />
          <Typography sx={{ color: '#8fb3d9', maxWidth: 500, mx: 'auto' }}>
            Tell us about your security needs and we'll get back to you within 24 hours with a custom quote.
          </Typography>
        </Box>

        <Grid container spacing={5}>
          {/* Contact Info Cards */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2.5}>
              {contactInfo.map((info, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper sx={{
                    p: 3,
                    background: 'rgba(13,40,71,0.6)',
                    border: '1px solid rgba(30,144,212,0.12)',
                    borderRadius: '12px',
                    '&:hover': {
                      border: `1px solid ${info.color}35`,
                      transform: 'translateY(-3px)',
                    },
                    transition: 'all 0.3s',
                  }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '10px',
                      background: `${info.color}15`,
                      border: `1px solid ${info.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: info.color, mb: 2,
                    }}>
                      {info.icon}
                    </Box>
                    <Typography sx={{ color: '#8fb3d9', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, mb: 0.5 }}>
                      {info.title}
                    </Typography>
                    <Typography sx={{ color: '#e8f0fe', fontWeight: 600, fontSize: '0.9rem', mb: 0.3 }}>
                      {info.value}
                    </Typography>
                    <Typography sx={{ color: '#8fb3d9', fontSize: '0.78rem' }}>
                      {info.sub}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Working hours */}
            <Paper sx={{
              p: 3, mt: 2.5,
              background: 'rgba(13,40,71,0.6)',
              border: '1px solid rgba(30,144,212,0.12)',
              borderRadius: '12px',
            }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
                <AccessTimeIcon sx={{ color: '#1e90d4' }} />
                <Typography sx={{ color: '#e8f0fe', fontWeight: 600 }}>Business Hours</Typography>
              </Box>
              {[
                ['Monday - Saturday', '9:00 AM - 7:00 PM'],
                ['Sunday', '10:00 AM - 2:00 PM'],
                ['Emergency Support', '24 / 7 Available'],
              ].map(([day, time], i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: i < 2 ? '1px solid rgba(30,144,212,0.08)' : 'none' }}>
                  <Typography sx={{ color: '#8fb3d9', fontSize: '0.85rem' }}>{day}</Typography>
                  <Typography sx={{ color: i === 2 ? '#2ecc71' : '#e8f0fe', fontSize: '0.85rem', fontWeight: 500 }}>{time}</Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Paper sx={{
              p: { xs: 4, md: 5 },
              background: 'rgba(13,40,71,0.7)',
              border: '1px solid rgba(30,144,212,0.2)',
              borderRadius: '16px',
              boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
            }}>
              <Typography variant="h5" sx={{ color: '#e8f0fe', fontWeight: 700, mb: 3 }}>
                Send us a Message
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Your Name *" name="name" value={form.name} onChange={handleChange} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Phone Number *" name="phone" value={form.phone} onChange={handleChange} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Email Address" name="email" value={form.email} onChange={handleChange} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline rows={4}
                      label="Describe your requirement"
                      name="message" value={form.message} onChange={handleChange} sx={inputSx}
                      placeholder="e.g. I need 4 cameras for my shop in Pune..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit" fullWidth variant="contained" size="large"
                      sx={{
                        py: 1.8, fontSize: '1rem', fontWeight: 600,
                        background: 'linear-gradient(135deg, #1e90d4, #0a5a8a)',
                        boxShadow: '0 8px 32px rgba(30,144,212,0.4)',
                        '&:hover': { boxShadow: '0 12px 40px rgba(30,144,212,0.6)', transform: 'translateY(-2px)' },
                        transition: 'all 0.3s',
                      }}
                    >
                      Send Message & Get Free Quote
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar open={submitted} autoHideDuration={5000} onClose={() => setSubmitted(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" sx={{ background: '#0d2847', color: '#e8f0fe', border: '1px solid #1e90d4' }}>
          ✅ Thank you! We'll contact you within 24 hours.
        </Alert>
      </Snackbar>
    </Box>
  );
}
